import { useEffect, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretLeft, CaretRight, Trash, PencilSimple, Repeat, Link as LinkIcon, Lightning, Warning, ChatCircleDots, MagnifyingGlass, Sparkle, BellRinging } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import CalendarSync from "@/components/CalendarSync";
import SmartSlotsCard from "@/components/SmartSlotsCard";
import TimeAnalyticsCard from "@/components/TimeAnalyticsCard";
import WellbeingImpactCard from "@/components/WellbeingImpactCard";
import GoalPlanCard from "@/components/GoalPlanCard";

dayjs.locale("ru");
const dayNames = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const COLORS = { blue: "#0084ff", green: "#00e676", yellow: "#ffea00", red: "#ff3b30", purple: "#a970ff", pink: "#ff6bd6" };
const REPEAT_LABELS = { daily: "ежедневно", weekly: "еженедельно", monthly: "ежемесячно" };
const ENERGY_COLORS = { green: "#00e676", yellow: "#ffea00", red: "#ff3b30" };
const CATEGORIES = ["Работа", "Спорт", "Личное", "Другое"];
const emptyForm = { title: "", time: "", repeat: "none", color: "blue", category: "", remind: "" };

export default function CalendarPage() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(dayjs());
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [energy, setEnergy] = useState({});
  const [showEnergy, setShowEnergy] = useState(() => localStorage.getItem("cal-energy") !== "0");
  const [weekLoad, setWeekLoad] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [quick, setQuick] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/events/search?q=${encodeURIComponent(query.trim())}`).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const quickCreate = async (e) => {
    e.preventDefault();
    if (!quick.trim()) return;
    setQuickBusy(true);
    try {
      const { data } = await api.post("/events/quick", { text: quick });
      toast.success(`Создано: «${data.title}» — ${dayjs(data.date).format("D MMMM")}${data.time ? `, ${data.time}` : ""} (${data.category})`);
      setQuick("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setQuickBusy(false); }
  };

  const dropOnDay = async (dateStr) => {
    if (!dragId) return;
    const ev = events.find((e) => e.id === dragId);
    if (!ev || ev.external || ev.recurring || ev.date === dateStr) { setDragId(null); return; }
    await api.put(`/events/${dragId}`, { date: dateStr });
    toast.success(`«${ev.title}» → ${dayjs(dateStr).format("D MMMM")}`);
    setDragId(null);
    load();
  };

  useEffect(() => {
    api.get("/calendar/week-load")
      .then((r) => setWeekLoad(r.data.weeks.filter((w) => w.level !== "ok")))
      .catch(() => {});
  }, []);

  const openAssistant = () => {
    localStorage.setItem("health-group-ai", "1");
    navigate("/health");
  };

  useEffect(() => {
    if (!showEnergy) return;
    api.get(`/calendar/energy?month=${cursor.format("YYYY-MM")}`).then((r) => setEnergy(r.data)).catch(() => {});
  }, [cursor, showEnergy]); // eslint-disable-line

  const toggleEnergy = () => setShowEnergy((s) => { localStorage.setItem("cal-energy", s ? "0" : "1"); return !s; });

  const months = view === "week"
    ? [...new Set([cursor.startOf("week").format("YYYY-MM"), cursor.endOf("week").format("YYYY-MM")])]
    : [cursor.format("YYYY-MM")];
  const load = async () => {
    try {
      const results = await Promise.all(months.map((m) => api.get(`/events?month=${m}`)));
      const seen = new Set();
      const merged = [];
      results.flatMap((r) => r.data).forEach((e) => {
        const k = `${e.id}-${e.date}`;
        if (!seen.has(k)) { seen.add(k); merged.push(e); }
      });
      setEvents(merged);
    } catch {}
  };
  useEffect(() => { load(); }, [cursor, view]); // eslint-disable-line

  const visibleEvents = catFilter
    ? events.filter((e) => catFilter === "Внешние" ? e.external : (e.category || (e.external ? "Внешние" : "Другое")) === catFilter)
    : events;
  const eventsFor = (d) => visibleEvents.filter((e) => e.date === d.format("YYYY-MM-DD"));
  const today = dayjs().format("YYYY-MM-DD");

  const monthCells = () => {
    const start = cursor.startOf("month");
    const offset = (start.day() + 6) % 7;
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= cursor.daysInMonth(); d++) cells.push(cursor.date(d));
    return cells;
  };
  const weekDays = () => {
    const start = cursor.startOf("week");
    return [...Array(7)].map((_, i) => start.add(i, "day"));
  };

  const nav = (dir) => setCursor(cursor.add(dir, view === "week" ? "week" : view === "day" ? "day" : "month"));
  const label = view === "week"
    ? `${cursor.startOf("week").format("D MMM")} — ${cursor.endOf("week").format("D MMM YYYY")}`
    : view === "day" ? cursor.format("D MMMM YYYY") : cursor.format("MMMM YYYY");

  const openDay = (d) => { setSelected(d); setEditing(null); setForm(emptyForm); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { title: form.title, time: form.time || null, repeat: form.repeat, color: form.color,
        category: form.category || null, remind: form.remind === "" ? null : +form.remind };
      if (editing) {
        await api.put(`/events/${editing}`, payload);
        toast.success("Событие обновлено");
      } else {
        await api.post("/events", { ...payload, date: selected.format("YYYY-MM-DD") });
        toast.success("Событие добавлено");
      }
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startEdit = (e) => {
    setEditing(e.id);
    setForm({ title: e.title, time: e.time || "", repeat: e.repeat || "none", color: e.color || "blue",
      category: e.category || "", remind: e.remind == null ? "" : String(e.remind) });
  };

  const removeEvent = async (id) => {
    await api.delete(`/events/${id}`);
    if (editing === id) { setEditing(null); setForm(emptyForm); }
    load();
  };

  const EventChip = ({ e }) => (
    <div draggable={!e.external && !e.recurring}
      onDragStart={(ev) => { ev.stopPropagation(); setDragId(e.id); }}
      className={`text-[11px] truncate px-1.5 py-0.5 rounded flex items-center gap-1 ${!e.external && !e.recurring ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{ backgroundColor: `${COLORS[e.color] || COLORS.blue}22`, color: COLORS[e.color] || COLORS.blue }}>
      {e.external && <LinkIcon size={9} className="shrink-0" />}
      {e.recurring && <Repeat size={9} className="shrink-0" />}
      {e.remind != null && <BellRinging size={9} className="shrink-0" />}
      <span className="truncate">{e.time ? `${e.time} ` : ""}{e.title}</span>
    </div>
  );

  return (
    <div className="fade-in" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Расписание</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Календарь</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-lg glass-panel border border-white/[0.07] p-0.5" data-testid="view-toggle">
            {[["month", "Месяц"], ["week", "Неделя"], ["day", "День"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={toggleEnergy} data-testid="energy-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${showEnergy ? "border-[#00e676]/40 text-[#00e676] bg-[#00e676]/10" : "border-white/[0.07] text-muted-foreground glass-panel"}`}>
            <Lightning size={13} weight={showEnergy ? "fill" : "regular"} /> Энергия
          </button>
          <button onClick={openAssistant} data-testid="calendar-ai-button"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.07] glass-panel text-xs font-semibold text-muted-foreground hover:text-primary transition-colors">
            <ChatCircleDots size={13} /> AI-ассистент
          </button>
          <CalendarSync onChanged={load} />
          <div className="flex items-center gap-1">
            <button onClick={() => nav(-1)} data-testid="calendar-prev-month" className="p-2 rounded-md hover:bg-secondary transition-colors"><CaretLeft size={18} /></button>
            <span className="font-heading font-semibold capitalize w-36 sm:w-44 text-center text-sm" data-testid="calendar-month-label">{label}</span>
            <button onClick={() => nav(1)} data-testid="calendar-next-month" className="p-2 rounded-md hover:bg-secondary transition-colors"><CaretRight size={18} /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <form onSubmit={quickCreate} className="flex-1 relative" data-testid="quick-create-form">
          <Sparkle size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
          <Input value={quick} onChange={(e) => setQuick(e.target.value)} disabled={quickBusy}
            placeholder="Быстро: «Обед с Аней завтра в 13:00» или «Тренировка в пятницу в 18»"
            className="pl-9 pr-20" data-testid="quick-create-input" />
          <button type="submit" disabled={quickBusy || !quick.trim()} data-testid="quick-create-submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 rounded-md bg-primary text-white text-xs font-semibold disabled:opacity-40">
            {quickBusy ? "…" : "Создать"}
          </button>
        </form>
        <div className="relative lg:w-72">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск событий…"
            className="pl-9" data-testid="event-search-input" />
          {results && (
            <div className="absolute z-40 mt-1 w-full glass-modal rounded-lg p-1 max-h-64 overflow-y-auto" data-testid="search-results">
              {results.length === 0 && <div className="text-xs text-muted-foreground p-3">Ничего не найдено</div>}
              {results.map((r) => (
                <button key={`${r.id}-${r.date}`} data-testid={`search-result-${r.id}`}
                  onClick={() => { setCursor(dayjs(r.date)); setQuery(""); setResults(null); openDay(dayjs(r.date)); }}
                  className="w-full flex items-center gap-2 text-left text-sm p-2 rounded-md hover:bg-white/[0.06] transition-colors">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[r.color] || COLORS.blue }} />
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="text-[10px] text-muted-foreground num shrink-0">{dayjs(r.date).format("D MMM YY")}{r.time ? ` ${r.time}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="category-filter">
        {["", ...CATEGORIES, "Внешние"].map((c) => (
          <button key={c || "all"} onClick={() => setCatFilter(c)} data-testid={`cat-filter-${c || "all"}`}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${catFilter === c ? "bg-primary/15 border-primary/40 text-primary" : "border-white/[0.07] text-muted-foreground hover:text-foreground"}`}>
            {c || "Все"}
          </button>
        ))}
      </div>

      {weekLoad.map((w) => (
        <div key={w.start} data-testid={`week-load-${w.level}`}
          className={`glass-card p-4 mb-4 flex items-start gap-3 ${w.level === "overload" ? "!border-[#ff3b30]/35" : "!border-[#ffea00]/35"}`}>
          <Warning size={20} weight="duotone" className={w.level === "overload" ? "text-[#ff3b30]" : "text-[#ffea00]"} />
          <div className="text-sm">
            <span className="font-semibold">{w.label} ({dayjs(w.start).format("D MMM")}–{dayjs(w.end).format("D MMM")}):</span>{" "}
            <span className="text-muted-foreground">{w.advice}</span>
          </div>
        </div>
      ))}

      {view === "day" ? (
        <div className="glass-card overflow-hidden" data-testid="day-view">
          {eventsFor(cursor).filter((e) => !e.time).length > 0 && (
            <div className="p-3 border-b border-border/40 flex flex-wrap gap-1.5">
              {eventsFor(cursor).filter((e) => !e.time).map((e, j) => <EventChip key={`${e.id}-${j}`} e={e} />)}
            </div>
          )}
          {[...Array(17)].map((_, i) => {
            const hour = 7 + i;
            const evs = eventsFor(cursor).filter((e) => e.time && parseInt(e.time.split(":")[0], 10) === hour);
            const isNow = cursor.format("YYYY-MM-DD") === today && new Date().getHours() === hour;
            return (
              <div key={hour} onClick={() => openDay(cursor)} data-testid={`day-hour-${hour}`}
                className={`flex gap-3 px-4 py-1.5 border-b border-border/20 cursor-pointer hover:bg-secondary/40 transition-colors min-h-[38px] ${isNow ? "bg-primary/5" : ""}`}>
                <span className={`text-xs num w-11 shrink-0 pt-0.5 ${isNow ? "text-primary font-bold" : "text-muted-foreground"}`}>{hour}:00</span>
                <div className="flex-1 space-y-1">
                  {evs.map((e, j) => <EventChip key={`${e.id}-${j}`} e={e} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "month" ? (
        <div className="glass-card overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 border-b border-border/40">
            {dayNames.map((d) => (
              <div key={d} className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground text-right">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells().map((d, i) => (
              <div key={i} onClick={() => d && openDay(d)}
                onDragOver={(ev) => d && ev.preventDefault()}
                onDrop={(ev) => { if (d) { ev.preventDefault(); dropOnDay(d.format("YYYY-MM-DD")); } }}
                data-testid={d ? `calendar-day-${d.format("YYYY-MM-DD")}` : undefined}
                className={`relative min-h-[56px] sm:min-h-[92px] p-1 sm:p-2 border-r border-b border-border/40 ${d ? "cursor-pointer hover:bg-secondary/50 transition-colors" : "bg-background/40"}`}>
                {d && (
                  <>
                    {showEnergy && energy[d.format("YYYY-MM-DD")]?.level && (
                      <span className="absolute bottom-1 left-1.5 right-1.5 h-[3px] rounded-full" data-testid={`energy-bar-${d.format("YYYY-MM-DD")}`}
                        style={{ backgroundColor: ENERGY_COLORS[energy[d.format("YYYY-MM-DD")].level], opacity: energy[d.format("YYYY-MM-DD")].kind === "forecast" ? 0.4 : 0.85 }} />
                    )}
                    <span className={`text-xs sm:text-sm num inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${d.format("YYYY-MM-DD") === today ? "bg-primary text-white font-bold" : "text-muted-foreground"}`}>
                      {d.date()}
                    </span>
                    <div className="mt-1 hidden sm:block space-y-0.5">
                      {eventsFor(d).slice(0, 3).map((e, j) => <EventChip key={`${e.id}-${j}`} e={e} />)}
                      {eventsFor(d).length > 3 && <div className="text-[10px] text-muted-foreground">+{eventsFor(d).length - 3}</div>}
                    </div>
                    {eventsFor(d).length > 0 && (
                      <div className="mt-1 flex gap-0.5 sm:hidden">
                        {eventsFor(d).slice(0, 3).map((e, j) => (
                          <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: COLORS[e.color] || COLORS.blue }} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="week-view">
          {weekDays().map((d) => {
            const evs = eventsFor(d);
            const isToday = d.format("YYYY-MM-DD") === today;
            return (
              <div key={d.format("YYYY-MM-DD")} onClick={() => openDay(d)}
                data-testid={`week-day-${d.format("YYYY-MM-DD")}`}
                className={`glass-card p-3 cursor-pointer min-h-[80px] sm:min-h-[180px] ${isToday ? "!border-primary/40" : ""}`}>
                <div className="flex sm:block items-center gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.format("dd")}</div>
                  <div className={`font-heading font-bold num ${isToday ? "text-primary" : ""}`}>{d.date()}</div>
                </div>
                <div className="space-y-1">
                  {evs.map((e, j) => <EventChip key={`${e.id}-${j}`} e={e} />)}
                  {evs.length === 0 && <div className="text-[10px] text-muted-foreground/50">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "month" && showEnergy && (
        <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground" data-testid="energy-legend">
          <span className="flex items-center gap-1.5"><span className="w-4 h-[3px] rounded-full bg-[#00e676]" /> высокая энергия</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-[3px] rounded-full bg-[#ffea00]" /> средняя</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-[3px] rounded-full bg-[#ff3b30]" /> низкая</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-[3px] rounded-full bg-[#00e676] opacity-40" /> прогноз</span>
        </div>
      )}

      <SmartSlotsCard onAdded={load} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TimeAnalyticsCard month={cursor.format("YYYY-MM")} />
        <WellbeingImpactCard />
      </div>

      <GoalPlanCard onApplied={load} />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">{selected?.format("D MMMM YYYY")}</DialogTitle>
          </DialogHeader>
          {selected && (
            <>
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {eventsFor(selected).map((e, j) => (
                  <div key={`${e.id}-${j}`} className="flex items-center gap-2 text-sm p-2 rounded-md bg-secondary" data-testid={`event-item-${e.id}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[e.color] || COLORS.blue }} />
                    <span className="flex-1 truncate">
                      {e.title}
                      {e.recurring && <span className="ml-1.5 text-[10px] text-muted-foreground">({REPEAT_LABELS[e.repeat]})</span>}
                      {e.external && <span className="ml-1.5 text-[10px] text-[#a970ff]" data-testid={`event-source-${e.id}`}>· {e.source}</span>}
                    </span>
                    {e.time && <span className="text-xs text-muted-foreground num">{e.time}</span>}
                    {!e.external && (
                      <>
                        <button onClick={() => startEdit(e)} data-testid={`event-edit-${e.id}`} className="text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={15} /></button>
                        <button onClick={() => removeEvent(e.id)} data-testid={`event-delete-${e.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={15} /></button>
                      </>
                    )}
                  </div>
                ))}
                {eventsFor(selected).length === 0 && <p className="text-sm text-muted-foreground">Нет событий</p>}
              </div>
              <form onSubmit={submit} className="space-y-3 pt-2 border-t border-border/60">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  {editing ? "Редактировать событие" : "Новое событие"}
                </div>
                <div className="space-y-1.5">
                  <Label>Название</Label>
                  <Input data-testid="event-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Встреча с командой" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Время</Label>
                    <Input data-testid="event-time-input" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Повтор</Label>
                    <select data-testid="event-repeat-select" value={form.repeat} onChange={(e) => setForm((f) => ({ ...f, repeat: e.target.value }))}
                      className="w-full h-9 rounded-md border border-input bg-secondary px-2 text-sm focus:outline-none">
                      <option value="none">Без повтора</option>
                      <option value="daily">Ежедневно</option>
                      <option value="weekly">Еженедельно</option>
                      <option value="monthly">Ежемесячно</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Категория</Label>
                    <select data-testid="event-category-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full h-9 rounded-md border border-input bg-secondary px-2 text-sm focus:outline-none">
                      <option value="">Авто</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Напоминание</Label>
                    <select data-testid="event-remind-select" value={form.remind} onChange={(e) => setForm((f) => ({ ...f, remind: e.target.value }))}
                      className="w-full h-9 rounded-md border border-input bg-secondary px-2 text-sm focus:outline-none">
                      <option value="">Нет</option>
                      <option value="15">За 15 минут</option>
                      <option value="30">За 30 минут</option>
                      <option value="60">За 1 час</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Цвет</Label>
                  <div className="flex gap-2" data-testid="event-color-picker">
                    {Object.entries(COLORS).map(([name, hex]) => (
                      <button key={name} type="button" onClick={() => setForm((f) => ({ ...f, color: name }))}
                        data-testid={`event-color-${name}`}
                        className={`w-6 h-6 rounded-full transition-transform ${form.color === name ? "scale-110 ring-2 ring-white/60" : "opacity-60 hover:opacity-100"}`}
                        style={{ backgroundColor: hex }} />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" data-testid="event-submit-button">
                    {editing ? "Сохранить" : "Добавить событие"}
                  </Button>
                  {editing && (
                    <Button type="button" variant="outline" onClick={() => { setEditing(null); setForm(emptyForm); }} data-testid="event-cancel-edit">
                      Отмена
                    </Button>
                  )}
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
